import { useQueryClient } from '@tanstack/react-query';
import { faSave } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  getGlobalSettingsGetSettingsQueryKey,
  useGlobalSettingsGetSettings,
  useGlobalSettingsUpdateSetting,
} from 'api/global-settings/global-settings';
import { GlobalSettingEnum } from 'api/model';
import Checkbox from 'components/inputs/Checkbox';
import MultiSelect from 'components/inputs/MultiSelect';
import TextButton from 'components/inputs/TextButton';
import useTranslations from 'contexts/basicData/useTranslations';
import React, { useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import styled from 'styled-components';
import { showException } from 'utils/exception-helper';
import GlobalSettingInput from './GlobalSettingInput';

export const Horizontal = styled.div`
  display: flex;
  gap: 20px;
  align-items: center;
`;

/**
 * Formulärplumbing + radfabriker för globala inställningar. Utbruten ur
 * GlobalSettings-sidan så att fler inställningsflikar (t.ex. Bokningsfilter Ride)
 * kan rendera settings-rader med samma auto-spar-beteende:
 * - checkboxar och (debouncade) inputs sparar direkt vid ändring
 * - multi-selects sparar via spar-ikonen som visas när valet skiljer sig från lagrat värde
 *
 * OBS: varje anropande sida får sin egen forminstans; hooken är inte delad state.
 */
const useGlobalSettingsForm = () => {
  const debounceTimeout = useRef<NodeJS.Timeout | null>(null);
  const { globalSettings: globalSettingsTranslations } = useTranslations();
  const queryClient = useQueryClient();
  const globalSettingsResponse = useGlobalSettingsGetSettings({
    query: {
      refetchInterval: 15000,
    },
  });
  const updateGlobalSettingsCall = useGlobalSettingsUpdateSetting();

  const updateglobalSettingsAsync = async (data: {
    key: GlobalSettingEnum;
    value: string;
  }) => {
    try {
      await updateGlobalSettingsCall.mutateAsync({ data });
    } catch (error) {
      showException(error);
    }
  };

  const { watch, reset, setValue } = useForm<{ [key: string]: string }>();

  useEffect(() => {
    reset(globalSettingsResponse.data);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalSettingsResponse.data]);

  const refetchSettings = async () => {
    await queryClient.invalidateQueries([
      `${getGlobalSettingsGetSettingsQueryKey()}`,
    ]);
    globalSettingsResponse.refetch();
  };

  const handleInputChangeDebounced = async (
    inputSetting: GlobalSettingEnum,
    value: string
  ) => {
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }

    debounceTimeout.current = setTimeout(async () => {
      await updateGlobalSettingsCall.mutateAsync({
        data: {
          key: inputSetting,
          value: value ?? 0,
        },
      });
      refetchSettings();
    }, 300);
  };

  const makeGlobalSettingsInput = (
    setting: GlobalSettingEnum,
    type: 'number' | 'text' | 'time' = 'number',
    options?: {
      min?: number;
      max?: number;
      step?: number | 'any';
      integer?: boolean;
    },
    info?: React.ReactNode,
    disabled?: boolean
  ) => ({
    key: globalSettingsTranslations[setting] ?? '',
    value: (
      <Horizontal>
        <GlobalSettingInput
          setting={setting}
          type={type}
          min={options?.min}
          max={options?.max}
          step={options?.step}
          integer={options?.integer}
          watch={watch}
          setValue={setValue}
          handleInputChangeDebounced={handleInputChangeDebounced}
          disabled={disabled}
        />
        {info}
      </Horizontal>
    ),
  });

  const makeGlobalSettingsCheckbox = (setting: GlobalSettingEnum) => ({
    key: globalSettingsTranslations[setting] ?? '',
    value: (
      <Checkbox
        checked={(watch(String(setting)) as unknown as string) === 'true'}
        disabled={globalSettingsResponse.isFetching}
        onChange={async (eve) => {
          await updateglobalSettingsAsync({
            key: setting,
            value: eve.currentTarget.checked.toString(),
          });
          refetchSettings();
        }}
      />
    ),
  });

  const mkGlobalSettingsSelect = (
    setting: GlobalSettingEnum,
    options: { label: string; value: any }[],
    type: 'number' | 'string'
  ) => ({
    key:
      globalSettingsTranslations[setting] ??
      'ÖVERSÄTTNING SAKNAS FÖR DETTA FÄLT',
    value: (
      <Horizontal>
        <MultiSelect
          onChange={(v) => {
            setValue(
              String(setting),
              Array.from(v)
                .filter((x) => {
                  if (type === 'number') {
                    return !Number.isNaN(x);
                  }
                  // Tomma strängar får inte joinas in — de ger CSV med inledande
                  // kommatecken (",Stockholm") som sedan visas som ", Stockholm".
                  return x !== '';
                })
                .join(',')
            );
          }}
          scrollable
          options={options}
          value={
            new Set(
              watch(String(setting))
                ?.split(',')
                // Split på tom sträng ger [""] — filtrera bort tomma poster så att
                // ett tomt lagrat värde inte blir ett "valt" tomt alternativ.
                ?.filter((v) => v !== '')
                ?.map((v) => {
                  if (type === 'number') {
                    return parseInt(v, 10);
                  }
                  return v;
                }) ?? []
            )
          }
          searchFn={(search, option) => {
            if (type === 'number') {
              return option.label.toLowerCase().includes(search.toLowerCase());
            }
            return option.label.toLowerCase().includes(search.toLowerCase());
          }}
        />
        {watch(String(setting)) !==
          globalSettingsResponse.data?.[String(setting)] && (
          <TextButton
            onClick={async () => {
              await updateglobalSettingsAsync({
                key: setting,
                value: watch(String(setting)),
              });
              refetchSettings();
            }}
          >
            <FontAwesomeIcon icon={faSave} />
          </TextButton>
        )}
      </Horizontal>
    ),
  });

  return {
    watch,
    setValue,
    globalSettingsResponse,
    updateGlobalSettingsCall,
    updateglobalSettingsAsync,
    refetchSettings,
    handleInputChangeDebounced,
    makeGlobalSettingsInput,
    makeGlobalSettingsCheckbox,
    mkGlobalSettingsSelect,
  };
};

export default useGlobalSettingsForm;
